// orderPaymentSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Utils from "utils";
import openNotification from "utils/notification";
import cloneDeep from "lodash";
import OrderPaymentService from "services/OrderPaymentService";

// (ReturnCode, OrderPaymentSummaryDto)
export const recordPayment = createAsyncThunk(
  "orderPayment/recordPayment",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await OrderPaymentService.recordPayment(payload);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title")
      );
      return response.data; // OrderPaymentSummaryDto
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// ReturnCode
export const completeTrip = createAsyncThunk(
  "orderPayment/completeTrip",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, tripId } = data;
      const response = await OrderPaymentService.completeTrip(tripId);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title")
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// OrderPaymentSummaryDto
export const getPaymentSummary = createAsyncThunk(
  "orderPayment/getPaymentSummary",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await OrderPaymentService.getPaymentSummary(orderId);
      return response.data; // OrderPaymentSummaryDto
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// List<OrderPaymentDto>
export const getPaymentHistory = createAsyncThunk(
  "orderPayment/getPaymentHistory",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await OrderPaymentService.getPaymentHistory(orderId);
      return response.data; // List<OrderPaymentDto>
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

const initialState = {
  loading: false,
  paymentSummary: null,  
  paymentHistory: [],    
};

export const orderPaymentSlice = createSlice({
  name: "orderPayment",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.loading = true;
    },
    clearPaymentSummary: (state) => {
      state.paymentSummary = null;
    },
    clearPaymentHistory: (state) => {
      state.paymentHistory = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // recordPayment
      .addCase(recordPayment.pending, (state) => { state.loading = true; })
      .addCase(recordPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentSummary = action.payload;
      })
      .addCase(recordPayment.rejected, (state) => { state.loading = false; })

      // completeTrip
      .addCase(completeTrip.pending, (state) => { state.loading = true; })
      .addCase(completeTrip.fulfilled, (state) => { state.loading = false; })
      .addCase(completeTrip.rejected, (state) => { state.loading = false; })

      // getPaymentSummary
      .addCase(getPaymentSummary.pending, (state) => { state.loading = true; })
      .addCase(getPaymentSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentSummary = action.payload;
      })
      .addCase(getPaymentSummary.rejected, (state) => { state.loading = false; })

      // getPaymentHistory
      .addCase(getPaymentHistory.pending, (state) => { state.loading = true; })
      .addCase(getPaymentHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentHistory = action.payload;
      })
      .addCase(getPaymentHistory.rejected, (state) => { state.loading = false; });
  },
});

export const { showLoading, clearPaymentSummary, clearPaymentHistory, } = orderPaymentSlice.actions;

export default orderPaymentSlice.reducer;